// Minimal image viewer for issue export pages
// Provides: modal open/close, prev/next navigation, keyboard controls, scroll lock, focus restoration

const IssueImageViewer = (() => {

	// consistent empty rendering for lines (use NBSP char, not HTML)
	function setTextOrNbsp(el, value) {
		if (!el) return;
		el.textContent = value ? String(value) : '\u00A0';
	}
	
	let imageUrls = [];
	let currentIndex = 0;
	let lastScrollY = 0;
	let lastFocusedEl = null;
    // --- Metadata Feature ---
    // `metas` stores an array of metadata objects, parallel to the `imageUrls` array.
    // Each object contains the description, assignment, priority, etc., for one image.
    let metas = [];
    // `metaEls` holds DOM references to the elements in the metadata section of the modal.
    // This avoids repeated `getElementById` calls in the `render` function.
    let metaEls = { desc: null, assignment: null, priority: null, priorityDot: null, created: null, rows: {} };

	// DOM refs
	let modal, imgEl, btnClose, btnPrev, btnNext, live;

	function init() {
		modal = CP.getel('imageModal');
		imgEl = CP.getel('imageModalImg');
		btnClose = CP.getel('imageModalClose');
		btnPrev = CP.getel('imageModalPrev');
		btnNext = CP.getel('imageModalNext');
		live = CP.getel('imageModalAriaLive');

        // --- Metadata Feature ---
        // Get references to all metadata elements within the modal.
        metaEls.desc = CP.getel('imageMetaDescription');
        metaEls.assignment = CP.getel('imageMetaAssignment');
        metaEls.priority = CP.getel('imageMetaPriority');
        metaEls.priorityDot = CP.getel('imageMetaPriorityDot');
        metaEls.created = CP.getel('imageMetaCreated');
        metaEls.rows = {
            assignment: CP.getel('imageMetaAssignmentRow'),
            priority: CP.getel('imageMetaPriorityRow')
        };

		if (!modal || !imgEl || !btnClose || !btnPrev || !btnNext) return;

		// Guard to prevent duplicate listeners if init is called again
		if (!modal.__ivWired) {
			btnClose.addEventListener('click', close);
			btnPrev.addEventListener('click', prev);
			btnNext.addEventListener('click', next);
			// Defensive: handle broken images gracefully
			imgEl.onerror = function() {
				if (live) live.textContent = 'Image failed to load';
			};
			modal.__ivWired = true;
		}
	}

    // --- Metadata Feature ---
    // Helper function to map priority color names (e.g., "Red") to hex codes
    // for styling the priority dot. Using hex codes directly is more robust than
    // relying on Tailwind class names, which might be purged.
    function priorityToHex(colorName) {
        switch ((colorName || '').toLowerCase()) {
            case 'red': return '#ce848d';
            case 'yellow': return '#d6c697';
            case 'green': return '#519670';
            case 'blue': return '#6a8bc7';
            default: return '#888888';       // grey for fallback/None
        }
    }

	function open(urls, startIndex, sourceEl) {
		if (!modal) init();
		if (!modal) return; // Modal not present on this page
		imageUrls = urls || [];
		currentIndex = Math.max(0, Math.min(startIndex || 0, imageUrls.length - 1));
		// Keep track of the element that triggered the viewer so we can restore focus on close
		lastFocusedEl = sourceEl || null;
		// Capture current scroll position so the background page can be restored exactly
		lastScrollY = window.scrollY || (document.documentElement && document.documentElement.scrollTop) || 0;

		// Robust scroll lock: fix the body in place without causing a jump to top
		// Rationale: Some browsers scroll the page when focus is moved to a newly shown element.
		// By setting body to position:fixed and offsetting it by the current scroll, the page
		// remains visually stationary while preventing background scrolling.
		document.body.style.position = 'fixed';
		document.body.style.top = '-' + lastScrollY + 'px';
		document.body.style.left = '0';
		document.body.style.right = '0';
		document.body.style.width = '100%';
		modal.classList.remove('hidden');
		window.addEventListener('keydown', onKey);
		render();
		// Focus the modal container (not the button) to avoid showing a focus border
		// while still keeping keyboard navigation active via global key handlers
		try { modal.focus({ preventScroll: true }); } catch (e) {}
	}

	function close() {
		window.removeEventListener('keydown', onKey);
		if (modal) modal.classList.add('hidden');
		// Restore body scroll position and styles to re-enable background scrolling
		document.body.style.position = '';
		document.body.style.top = '';
		document.body.style.left = '';
		document.body.style.right = '';
		document.body.style.width = '';
		window.scrollTo({ top: lastScrollY });
		if (lastFocusedEl) {
			try { lastFocusedEl.focus(); } catch (e) {}
		}
	}

	function onKey(e) {
		if (e.key === 'Escape') return close();
		if (e.key === 'ArrowLeft') return prev();
		if (e.key === 'ArrowRight') return next();
	}

	function prev() {
		if (currentIndex > 0) {
			currentIndex--;
			render();
		}
	}

	function next() {
		if (currentIndex < imageUrls.length - 1) {
			currentIndex++;
			render();
		}
	}

	function render() {
		if (!imgEl) return;
		imgEl.src = imageUrls[currentIndex] || '';
		if (btnPrev) btnPrev.style.display = currentIndex === 0 ? 'none' : '';
		if (btnNext) btnNext.style.display = currentIndex === imageUrls.length - 1 ? 'none' : '';
		if (live) live.textContent = 'Image ' + (currentIndex + 1) + ' of ' + imageUrls.length;

        // --- Metadata Feature ---
        // Populate the metadata section based on the current image index.
        const m = metas[currentIndex] || {};
        setTextOrNbsp(metaEls.desc, m.desc);
        setTextOrNbsp(metaEls.created, m.created);
        setTextOrNbsp(metaEls.assignment, m.assignment);
        setTextOrNbsp(metaEls.priority, m.priority);
        if (metaEls.priorityDot) metaEls.priorityDot.style.backgroundColor = priorityToHex(m.priorityColor);
	}

	// Delegated wiring: attach a single listener to the container
	// This stays robust if the issue list re-renders; we compute the gallery set at click time
	function wireDelegated(container) {
		if (!container) return;
		init();

		// Guard to avoid stacking multiple click listeners on the same container
		if (container.__ivThumbWired) return;
		container.__ivThumbWired = true;

		container.addEventListener('click', (e) => {
			const thumb = e.target && e.target.closest ? e.target.closest('[data-role="issue-thumb"]') : null;
			if (!thumb || !container.contains(thumb)) return;
			// Prevent default image/link behavior that could cause unintended scroll or focus changes
			e.preventDefault();
			const thumbs = Array.from(container.querySelectorAll('[data-role="issue-thumb"]'));
			const urls = thumbs.map(t => t.dataset.full || t.src);
            // --- Metadata Feature ---
            // At the moment the user clicks a thumbnail, we gather the metadata from the `data-*`
            // attributes of *all* thumbnails in the list. This builds the `metas` array which
            // the `render` function will use to display data for the currently viewed image.
            metas = thumbs.map(t => ({
                desc: t.dataset.desc || '',
                assignment: t.dataset.assignment || '',
                priority: t.dataset.priority || '',
                priorityColor: t.dataset.priorityColor || t.dataset['priorityColor'] || '',
                created: t.dataset.created || ''
            }));
			const idx = thumbs.indexOf(thumb);
			if (idx >= 0) open(urls, idx, thumb);
		});
	}

	return { init, open, close, wireDelegated };
})();

// Expose globally for page scripts
window.IssueImageViewer = IssueImageViewer;


